import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/Toast';
import { salesAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Plus, Trash2, Save, X, FileText, Loader2 } from 'lucide-react';
import { SimpleModal } from '@/components/SimpleModal';

const PAYMENT_METHODS = [
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'pix', label: 'PIX' },
  { value: 'cartao_credito', label: 'Cartão de Crédito' },
  { value: 'cartao_debito', label: 'Cartão de Débito' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'transferencia', label: 'Transferência' },
  { value: 'vale_alimentacao', label: 'Vale Alimentação' },
  { value: 'vale_refeicao', label: 'Vale Refeição' },
];

export const EditSaleModal = ({ sale, open, onClose, onSaved }) => {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [emitindo, setEmitindo] = useState(false);
  const [nfceNumber, setNfceNumber] = useState('');
  const [useContingencia, setUseContingencia] = useState(false);

  useEffect(() => {
    if (sale) {
      setItems(sale.items.map(item => ({
        ...item,
        product_id: item.product_id || `item-${Date.now()}`,
      })));
      setPaymentMethods(sale.payment_methods || [{ method: 'dinheiro', amount: sale.total }]);
      setDiscount(sale.discount || 0);
      setNfceNumber(sale.nfce_number ? String(sale.nfce_number) : '');
      setUseContingencia(false);
    }
  }, [sale]);

  const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
  const total = subtotal - discount;

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    const item = newItems[index];
    
    if (field === 'quantity' || field === 'unit_price') {
      const numValue = parseFloat(value) || 0;
      item[field] = numValue;
      item.total = item.quantity * item.unit_price;
    } else {
      item[field] = value;
    }
    
    setItems(newItems);
  };

  const handleAddItem = () => {
    setItems([...items, {
      product_id: `novo-${Date.now()}`,
      product_name: '',
      quantity: 1,
      unit_price: 0,
      total: 0,
    }]);
  };

  const handleRemoveItem = (index) => {
    if (items.length <= 1) {
      toast.error('A venda deve ter pelo menos um item');
      return;
    }
    setItems(items.filter((_, i) => i !== index));
  };

  const handlePaymentChange = (index, field, value) => {
    const newPayments = [...paymentMethods];
    if (field === 'amount') {
      newPayments[index].amount = parseFloat(value) || 0;
    } else {
      newPayments[index].method = value;
    }
    setPaymentMethods(newPayments);
  };

  const handleAddPayment = () => {
    const remaining = total - paymentMethods.reduce((sum, pm) => sum + pm.amount, 0);
    setPaymentMethods([...paymentMethods, { method: 'dinheiro', amount: Math.max(0, remaining) }]);
  };

  const handleRemovePayment = (index) => {
    if (paymentMethods.length <= 1) {
      toast.error('Deve haver pelo menos uma forma de pagamento');
      return;
    }
    setPaymentMethods(paymentMethods.filter((_, i) => i !== index));
  };

  const handleAutoDistribute = () => {
    if (paymentMethods.length === 1) {
      setPaymentMethods([{ ...paymentMethods[0], amount: total }]);
    }
  };

  const handleSave = async () => {
    if (items.some(item => !item.product_name)) {
      toast.error('Todos os itens devem ter um nome');
      return;
    }
    if (items.some(item => item.quantity <= 0 || item.unit_price <= 0)) {
      toast.error('Quantidade e preço devem ser maiores que zero');
      return;
    }
    
    const totalPago = paymentMethods.reduce((sum, pm) => sum + pm.amount, 0);
    if (Math.abs(totalPago - total) > 0.01) {
      toast.error(`Valor pago (${formatCurrency(totalPago)}) não corresponde ao total (${formatCurrency(total)})`);
      return;
    }

    setSaving(true);
    try {
      await salesAPI.update(sale.id, {
        items: items.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total: item.total,
        })),
        payment_methods: paymentMethods,
        discount,
      });
      setSaving(false);
      onSaved?.();
      setTimeout(() => {
        toast.success('Venda atualizada com sucesso!');
        onClose();
      }, 50);
    } catch (error) {
      setSaving(false);
      const errorMsg = error.response?.data?.detail || 'Erro ao salvar venda';
      setTimeout(() => {
        toast.error(errorMsg);
      }, 50);
    }
  };

  const handleSaveAndEmit = async () => {
    const totalPago = paymentMethods.reduce((sum, pm) => sum + pm.amount, 0);
    if (Math.abs(totalPago - total) > 0.01) {
      toast.error(`Valor pago (${formatCurrency(totalPago)}) não corresponde ao total (${formatCurrency(total)})`);
      return;
    }

    // Validar número da NFC-e se foi alterado
    const newNumber = nfceNumber ? parseInt(nfceNumber) : null;
    if (nfceNumber && (isNaN(newNumber) || newNumber <= 0)) {
      toast.error('Número da NFC-e inválido');
      return;
    }

    setSaving(true);
    try {
      // Salvar venda com o novo número se alterado
      await salesAPI.update(sale.id, {
        items: items.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total: item.total,
        })),
        payment_methods: paymentMethods,
        discount,
        nfce_number: newNumber || sale.nfce_number,
      });
      
      setEmitindo(true);
      
      // Emitir NFC-e (com contingência se marcado)
      const response = await salesAPI.emitirNfceComOpcoes(sale.id, {
        novo_numero: newNumber,
        contingencia: useContingencia
      });
      
      setSaving(false);
      setEmitindo(false);
      
      if (response.data.sucesso) {
        onSaved?.();
        const isContingencia = response.data.contingencia || response.data.nfce_simulado;
        const numeroAjustado = response.data.numero_ajustado;
        setTimeout(() => {
          if (isContingencia) {
            toast.success(`NFC-e ${response.data.nfce_number} emitida em CONTINGÊNCIA! Sincronizar depois.`);
          } else if (numeroAjustado) {
            toast.success(`NFC-e ${response.data.nfce_number} autorizada! (número ajustado automaticamente por duplicidade)`);
          } else {
            toast.success(`NFC-e ${response.data.nfce_number} autorizada! Protocolo: ${response.data.nfce_protocol}`);
          }
          onClose();
        }, 50);
      } else {
        setTimeout(() => {
          toast.error(response.data.mensagem || 'Erro ao emitir NFC-e');
        }, 50);
      }
    } catch (error) {
      setSaving(false);
      setEmitindo(false);
      const errorMsg = error.response?.data?.detail || 'Erro ao processar';
      
      // Se falhou, sugerir contingência
      if (!useContingencia) {
        setTimeout(() => {
          toast.error(`${errorMsg}. Tente ativar o modo Contingência Offline.`);
        }, 50);
      } else {
        setTimeout(() => {
          toast.error(errorMsg);
        }, 50);
      }
    }
  };

  if (!sale) return null;

  const isEditable = !sale.nfce_key || sale.nfce_simulado || sale.nfce_status === 'erro' || sale.nfce_status === 'pendente' || !sale.nfce_status;

  const footerButtons = (
    <>
      <Button variant="outline" onClick={onClose} data-testid="cancel-edit-btn">
        <X className="w-4 h-4 mr-2" />
        Cancelar
      </Button>
      {isEditable && (
        <>
          <Button 
            onClick={handleSave} 
            disabled={saving || emitindo}
            variant="outline"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="save-sale-btn"
          >
            {saving && !emitindo ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Salvar
          </Button>
          <Button 
            onClick={handleSaveAndEmit} 
            disabled={saving || emitindo}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="save-emit-btn"
          >
            {emitindo ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" />
            )}
            Salvar e Emitir NFC-e
          </Button>
        </>
      )}
    </>
  );

  return (
    <SimpleModal 
      open={open} 
      onClose={onClose} 
      title={`Editar Venda ${sale.nfce_number ? `#${sale.nfce_number}` : ''}`}
      footer={footerButtons}
      maxWidth="max-w-3xl"
    >
      <div className="space-y-6" data-testid="edit-sale-modal">
        {!isEditable && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-amber-700 text-sm">
              Esta venda já possui NFC-e autorizada pela SEFAZ e não pode ser editada.
              Para correções, é necessário cancelar a nota e emitir uma nova.
            </p>
          </div>
        )}

        {/* Número da NFC-e e Opções de Emissão */}
        {isEditable && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-blue-800">Número da NFC-e</Label>
                <Input
                  type="number"
                  min="1"
                  value={nfceNumber}
                  onChange={(e) => setNfceNumber(e.target.value)}
                  placeholder="Número será gerado automaticamente"
                  className="mt-1 bg-white"
                  data-testid="nfce-number-input"
                />
                <p className="text-xs text-blue-600 mt-1">
                  Altere aqui se precisar usar um número específico
                </p>
              </div>
              <div className="flex items-center">
                <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg hover:bg-blue-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={useContingencia}
                    onChange={(e) => setUseContingencia(e.target.checked)}
                    className="w-5 h-5 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                    data-testid="contingencia-checkbox"
                  />
                  <div>
                    <span className="font-semibold text-blue-800">Contingência Offline</span>
                    <p className="text-xs text-blue-600">Emite a nota offline se a SEFAZ estiver indisponível</p>
                  </div>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Itens */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <Label className="text-base font-semibold">Itens da Venda</Label>
            {isEditable && (
              <Button size="sm" variant="outline" onClick={handleAddItem} data-testid="add-item-btn">
                <Plus className="w-4 h-4 mr-1" />
                Adicionar Item
              </Button>
            )}
          </div>
          
          <div className="space-y-3">
            {items.map((item, index) => (
              <div key={index} className="bg-slate-50 rounded-lg p-3" data-testid={`item-row-${index}`}>
                <div className="grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-5">
                    <Label className="text-xs text-slate-500">Produto</Label>
                    <Input
                      value={item.product_name}
                      onChange={(e) => handleItemChange(index, 'product_name', e.target.value)}
                      placeholder="Nome do produto"
                      disabled={!isEditable}
                      data-testid={`item-name-${index}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-500">Qtd</Label>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      disabled={!isEditable}
                      data-testid={`item-qty-${index}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-500">Preço Unit.</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unit_price}
                      onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                      disabled={!isEditable}
                      data-testid={`item-price-${index}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-500">Total</Label>
                    <div className="h-9 flex items-center font-semibold text-emerald-600">
                      {formatCurrency(item.total)}
                    </div>
                  </div>
                  <div className="col-span-1 flex justify-end">
                    {isEditable && items.length > 1 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveItem(index)}
                        className="text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                        data-testid={`remove-item-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Separator />

        {/* Desconto */}
        <div className="flex items-center gap-4">
          <Label className="font-semibold">Desconto:</Label>
          <div className="w-32">
            <Input
              type="number"
              min="0"
              step="0.01"
              value={discount}
              onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
              disabled={!isEditable}
              data-testid="discount-input"
            />
          </div>
          <span className="text-slate-600">
            Subtotal: {formatCurrency(subtotal)} | Total: <span className="font-bold text-emerald-600">{formatCurrency(total)}</span>
          </span>
        </div>

        <Separator />

        {/* Formas de Pagamento */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <Label className="text-base font-semibold">Formas de Pagamento</Label>
            {isEditable && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleAutoDistribute} data-testid="auto-distribute-btn">
                  Ajustar Valor
                </Button>
                <Button size="sm" variant="outline" onClick={handleAddPayment} data-testid="add-payment-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              </div>
            )}
          </div>
          
          <div className="space-y-3">
            {paymentMethods.map((pm, index) => (
              <div key={index} className="bg-blue-50 rounded-lg p-3" data-testid={`payment-row-${index}`}>
                <div className="grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-6">
                    <Label className="text-xs text-slate-500">Forma de Pagamento</Label>
                    {/* Select nativo ao invés do Radix UI */}
                    <select
                      value={pm.method}
                      onChange={(e) => handlePaymentChange(index, 'method', e.target.value)}
                      disabled={!isEditable}
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                      data-testid={`payment-method-${index}`}
                    >
                      {PAYMENT_METHODS.map(method => (
                        <option key={method.value} value={method.value}>
                          {method.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-4">
                    <Label className="text-xs text-slate-500">Valor</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={pm.amount}
                      onChange={(e) => handlePaymentChange(index, 'amount', e.target.value)}
                      disabled={!isEditable}
                      data-testid={`payment-amount-${index}`}
                    />
                  </div>
                  <div className="col-span-2 flex justify-end pt-5">
                    {isEditable && paymentMethods.length > 1 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemovePayment(index)}
                        className="text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                        data-testid={`remove-payment-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Resumo do pagamento */}
          <div className="mt-3 p-3 bg-slate-100 rounded-lg">
            <div className="flex justify-between text-sm">
              <span>Total a Pagar:</span>
              <span className="font-bold">{formatCurrency(total)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Total Informado:</span>
              <span className={`font-bold ${Math.abs(paymentMethods.reduce((sum, pm) => sum + pm.amount, 0) - total) > 0.01 ? 'text-rose-600' : 'text-emerald-600'}`}>
                {formatCurrency(paymentMethods.reduce((sum, pm) => sum + pm.amount, 0))}
              </span>
            </div>
          </div>
        </div>
      </div>
    </SimpleModal>
  );
};
